import { getNetworkDisplayName, type NetworkType, useIntl, useSettings } from '@capsicum/common';

import { CollectibleOwnerType, type DetailsTableItemType } from '../models';
import { getCollectiblePropertiesItems, getCollectibleSerialNumber } from '../util';

import { useMyCollectible } from './useMyCollectible';

const useCollectibleDetails = (collectibleId: string) => {
	const { $t } = useIntl();
	const { areSettingsLoading, settings } = useSettings();

	const texts = {
		blockchainLabel: $t('digitalCollectibles.details.blockchainLabel'),
		collectibleBackgroundAltText: $t('digitalCollectibles.details.collectibleBackgroundImageAltText'),
		collectibleImageAltText: $t('digitalCollectibles.details.collectibleImageAltText'),
		contractAddressLabel: $t('digitalCollectibles.details.contractAddressLabel'),
		detailsTableTitle: $t('digitalCollectibles.details.title'),
		ownedByLabel: $t('digitalCollectibles.details.ownedByLabel'),
		propertiesTableTitle: $t('digitalCollectibles.details.propertiesTableTitle'),
		serialNumberLabel: $t('digitalCollectibles.details.serialNumberLabel'),
		tokenIdLabel: $t('digitalCollectibles.details.tokenIdLabel')
	};

	const { data: collectible, error, isLoading } = useMyCollectible(collectibleId);

	// PROPERTIES TABLE CONSTRUCTION
	const propertiesTableItems = getCollectiblePropertiesItems(collectible);

	// DETAILS TABLE CONSTRUCTION
	const detailsTableItems: Array<DetailsTableItemType> = [
		{
			label: texts.serialNumberLabel,
			type: 'text',
			value: getCollectibleSerialNumber(collectible)
		},
		{
			label: texts.ownedByLabel,
			type: 'text',
			value: collectible?.owner?.ownerLabel || ''
		},
		{
			label: texts.contractAddressLabel,
			testID: 'digitalCollectibles-details-copyContractAddress',
			type: 'copy',
			value: collectible?.blockchain?.smartContractAddress || ''
		},
		{
			label: texts.tokenIdLabel,
			testID: 'digitalCollectibles-details-copyTokenId',
			type: 'copy',
			value: collectible?.blockchain?.tokenId || ''
		},
		{
			label: texts.blockchainLabel,
			type: 'text',
			value: getNetworkDisplayName(collectible?.blockchain?.networkType as NetworkType)
		}
	];

	// TODO: remove casting CollectibleOwnerType once NftFormattedCollectibleOwnerDtoOwnerType is updated with 'ncw' value
	const isNFTMigrated = (collectible?.owner?.ownerType as CollectibleOwnerType) === CollectibleOwnerType.NCW;

	// different config to show/hide the withdraw button depending on the wallet type
	const hideWithdrawButton =
		areSettingsLoading || (!isNFTMigrated && !settings.isDigitalCollectiblesWithdrawalEnabled);

	return {
		animationUrl: collectible?.summary?.animationUrl || '',
		description: collectible?.summary?.description || '',
		detailsTableItems,
		errorLoadingCollectible: error,
		hideWithdrawButton,
		imageUrl: collectible?.summary?.imageUrl || '',
		isCollectibleExists: Boolean(collectible?.blockchain?.collectionId),
		isLoadingCollectible: isLoading,
		name: collectible?.summary?.name || '',
		networkType: collectible?.blockchain?.networkType as NetworkType,
		partner: {
			banner: collectible?.partner?.banner || '',
			id: collectible?.partner?.id || '',
			logo: collectible?.partner?.logo || '',
			name: collectible?.partner?.name || '',
			rewardsBackground: collectible?.partner?.rewardsBackground || ''
		},
		propertiesTableItems,
		texts
	};
};

export { useCollectibleDetails };
